import { Container, Text } from "@fluidattacks/design";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useTheme } from "styled-components";

import { states } from "./animation-icons";

import { Message } from "../message";
import { useChatStore } from "../use-chat-store";
import { SkeletonLoader } from "components/skeleton-loader";
import { useIconAnimation } from "hooks/use-icon-animations";

const svgStates = Object.values(states);

const Messages: FC = (): JSX.Element => {
  const theme = useTheme();
  const { messages, isLoading, error } = useChatStore();

  const { currentState: currentIndex, activate } = useIconAnimation(
    svgStates.length,
  );

  useEffect((): void => {
    if (isLoading) {
      activate();
    }
  }, [isLoading, activate]);

  const endRef = useRef<HTMLDivElement>(null);
  useEffect((): void => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  return (
    <Container
      alignItems={"flex-end"}
      bgColor={theme.palette.white}
      display={"flex"}
      flexDirection={"column"}
      gap={1}
      height={"100%"}
      justify={"space-between"}
      scroll={"y"}
      width={"100%"}
    >
      {messages.map(
        ({ content, role }, index): JSX.Element => (
          <Message content={content} key={index} role={role} />
        ),
      )}
      {isLoading && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            width: "100%",
          }}
        >
          <Container display={"flex"} gap={0.5} width={"100%"}>
            <span style={{ verticalAlign: "middle" }}>
              <svg
                dangerouslySetInnerHTML={{
                  __html: svgStates[currentIndex],
                }}
                fill={"none"}
                height={"16"}
                viewBox={"0 0 65 65"}
                width={"16"}
                xmlns={"http://www.w3.org/2000/svg"}
              />
            </span>
            <Text size={"md"}>{"Just a moment..."}</Text>
          </Container>
          <SkeletonLoader />
        </div>
      )}
      {error === null ? null : (
        <p style={{ color: "#bf0b1a", margin: 8 }}>{error}</p>
      )}
      <div ref={endRef} style={{ height: "20px" }} />
    </Container>
  );
};

export { Messages };
